/**
 * Admin / Executive Task Command Center (Section 5).
 *
 * Unified cross-department task view built from
 *   GET /api/role-insights/task-command-center
 *
 * Surfaces pending, late and critical work across CX, QC, GTM, GTM Sales
 * Leader, CX Leader and BFR — so leadership can see every bottleneck in
 * one place.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Loader2, RefreshCw, Headset, ShieldCheck, Briefcase, Crown,
  Users, Banknote, AlertTriangle,
} from 'lucide-react';
import { roleInsightsAPI } from '../../services/api';
import CompletedTimeCell from '../../components/CompletedTimeCell';
import DateRangeFilter, { rangeParams } from '../../components/DateRangeFilter';
import { useTaskTable, TaskSearchBar, TablePagination, matchRow } from '../../components/tasks/TableControls';

const OVERDUE_PALETTE = {
  Late:     'bg-amber-100 text-amber-700 border-amber-200',
  Urgent:   'bg-orange-100 text-orange-700 border-orange-200',
  Critical: 'bg-rose-100 text-rose-700 border-rose-200',
};

const STATUS_BADGE = {
  assigned:     'bg-blue-100 text-blue-700',
  in_progress:  'bg-amber-100 text-amber-700',
  under_review: 'bg-amber-100 text-amber-700',
  rejected:     'bg-red-100 text-red-700',
  completed:    'bg-emerald-100 text-emerald-700',
};

const OverdueBadge = ({ label }) => {
  const l = (label || '').trim();
  if (!l) return <span className="text-gray-300 text-xs">—</span>;
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${OVERDUE_PALETTE[l] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>{l}</span>;
};

const Section = ({ title, Icon, accent, count, testId, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid={testId}>
    <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent.bg}`}>
        <Icon size={16} className={accent.fg} />
      </div>
      <h3 className="text-sm font-bold text-gray-800">{title}</h3>
      {count != null && (
        <span className="ml-1 px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full text-[11px] font-medium" data-testid={`${testId}-count`}>{count}</span>
      )}
    </div>
    {children}
  </div>
);

const Empty = ({ cols, label = 'No pending items.' }) => (
  <tr><td colSpan={cols} className="px-4 py-6 text-center text-gray-400 text-xs">{label}</td></tr>
);

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const TaskCommandCenter = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Item 9 — Tasks date filter has NO default (user picks).
  const [range, setRange] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await roleInsightsAPI.taskCommandCenter(rangeParams(range, customFrom, customTo));
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [range, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  const cx = data?.cx || { employees: [], rows: [] };
  const qc = data?.qc || { employees: [], rows: [] };
  const gtm = data?.gtm || { rows: [] };
  const gtmLeader = data?.gtm_sales_leader || { rows: [] };
  const cxLeader = data?.cx_leader || { rows: [] };
  const bfr = data?.bfr || { approvals: [], offboarding_finance: [] };
  // Item 8 — CX Leader must ONLY see CX/QC sections (no GTM / BFR tasks).
  const isCxLeader = data?.role === 'cx_leader';

  // Single property search across every section + independent pagination per
  // table. Section header counts stay derived from the full datasets above.
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const flt = (rows, keys) => (q ? (rows || []).filter((r) => matchRow(r, keys, q)) : (rows || []));
  const bfrRows = [...(bfr.approvals || []), ...(bfr.offboarding_finance || [])];
  const cxT = useTaskTable(flt(cx.rows, ['listing_name', 'employee']), { pageSize: 10, resetKey: q });
  const qcT = useTaskTable(flt(qc.rows, ['listing_name', 'qc_owner']), { pageSize: 10, resetKey: q });
  const gtmT = useTaskTable(flt(gtm.rows, ['account_name', 'lead_owner']), { pageSize: 10, resetKey: q });
  const gtmLeaderT = useTaskTable(flt(gtmLeader.rows, ['account_name']), { pageSize: 10, resetKey: q });
  const cxLeaderT = useTaskTable(flt(cxLeader.rows, ['property_name']), { pageSize: 10, resetKey: q });
  const bfrT = useTaskTable(flt(bfrRows, ['account_name', 'type']), { pageSize: 10, resetKey: q });

  return (
    <div className="space-y-5" data-testid="task-command-center">
      <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Task Command Center</h2>
          <p className="text-sm text-gray-500 mt-0.5">Pending, late &amp; critical work across every department.</p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          data-testid="tcc-refresh">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3" data-testid="tcc-error">{error}</div>}

      <DateRangeFilter value={range} onChange={setRange} customFrom={customFrom} customTo={customTo}
        onCustomFrom={setCustomFrom} onCustomTo={setCustomTo} testId="tcc-date-filter" />

      <TaskSearchBar query={query} setQuery={setQuery} placeholder="Search by listing / account / property…" testId="tcc-search" />

      {loading && !data ? (
        <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 size={24} className="animate-spin" /></div>
      ) : (
        <>
          {/* CX */}
          <Section title="CX — Listings" Icon={Headset} accent={{ bg: 'bg-teal-50', fg: 'text-teal-600' }} count={cx.rows.length} testId="tcc-cx">
            {cx.employees.length > 0 && (
              <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 border-b border-gray-50">
                {cx.employees.map((e, i) => (
                  <div key={i} className="rounded-lg border border-gray-100 p-2.5" data-testid={`tcc-cx-emp-${i}`}>
                    <p className="text-xs font-semibold text-gray-700 truncate">{e.employee}</p>
                    <p className="text-[11px] text-gray-500 mt-1">Pending <span className="font-bold text-amber-600">{e.pending}</span> · Review <span className="font-bold text-amber-500">{e.under_review}</span></p>
                    <p className="text-[11px] text-gray-500">Overdue <span className="font-bold text-red-600">{e.overdue}</span> · Done <span className="font-bold text-emerald-600">{e.completed}</span></p>
                  </div>
                ))}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tcc-cx-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left">Listing</th>
                    <th className="px-4 py-2.5 text-left">CX</th>
                    <th className="px-4 py-2.5 text-left">Status</th>
                    <th className="px-4 py-2.5 text-left">Assigned</th>
                    <th className="px-4 py-2.5 text-left">Working Time</th>
                    <th className="px-4 py-2.5 text-left">Completed Time</th>
                    <th className="px-4 py-2.5 text-left">Overdue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {cxT.total === 0 && <Empty cols={7} />}
                  {cxT.pageRows.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-gray-800">{r.listing_name}</td>
                      <td className="px-4 py-2.5 text-gray-700">{r.employee}</td>
                      <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_BADGE[r.status] || 'bg-gray-100 text-gray-600'}`}>{(r.status || '').replace('_', ' ')}</span></td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">{fmtDate(r.assigned_at)}</td>
                      <td className="px-4 py-2.5 text-gray-600 text-xs">{r.working_time || '—'}</td>
                      <td className="px-4 py-2.5"><CompletedTimeCell ct={r.completed_time} /></td>
                      <td className="px-4 py-2.5"><OverdueBadge label={r.overdue_label} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TablePagination page={cxT.page} totalPages={cxT.totalPages} setPage={cxT.setPage} total={cxT.total} pageSize={cxT.pageSize} testId="tcc-cx-pagination" />
          </Section>

          {/* QC */}
          <Section title="QC — Quality Checks" Icon={ShieldCheck} accent={{ bg: 'bg-indigo-50', fg: 'text-indigo-600' }} count={qc.rows?.length || 0} testId="tcc-qc">
            {qc.employees?.length > 0 && (
              <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 border-b border-gray-50">
                {qc.employees.map((e, i) => (
                  <div key={i} className="rounded-lg border border-gray-100 p-2.5" data-testid={`tcc-qc-emp-${i}`}>
                    <p className="text-xs font-semibold text-gray-700 truncate">{e.employee}</p>
                    <p className="text-[11px] text-gray-500 mt-1">Pending <span className="font-bold text-amber-600">{e.pending}</span> · Done <span className="font-bold text-emerald-600">{e.completed}</span></p>
                    <p className="text-[11px] text-gray-500">Overdue <span className="font-bold text-red-600">{e.overdue}</span> · Assigned <span className="font-bold text-gray-700">{e.assigned}</span></p>
                  </div>
                ))}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tcc-qc-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left">Listing</th>
                    <th className="px-4 py-2.5 text-left">QC Owner</th>
                    <th className="px-4 py-2.5 text-left">Status</th>
                    <th className="px-4 py-2.5 text-left">Assigned Time</th>
                    <th className="px-4 py-2.5 text-left">Working Time</th>
                    <th className="px-4 py-2.5 text-left">Completed Time</th>
                    <th className="px-4 py-2.5 text-left">Overdue Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {qcT.total === 0 && <Empty cols={7} />}
                  {qcT.pageRows.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50" data-testid={`tcc-qc-row-${i}`}>
                      <td className="px-4 py-2.5 font-medium text-gray-800">{r.listing_name}</td>
                      <td className="px-4 py-2.5 text-gray-700">{r.qc_owner}</td>
                      <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_BADGE[r.status] || 'bg-gray-100 text-gray-600'}`}>{(r.status || '').replace('_', ' ')}</span></td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">{fmtDate(r.assigned_at)}</td>
                      <td className="px-4 py-2.5 text-gray-600 text-xs">{r.working_time || '—'}</td>
                      <td className="px-4 py-2.5"><CompletedTimeCell ct={r.completed_time} /></td>
                      <td className="px-4 py-2.5 text-xs whitespace-nowrap">{r.overdue_time ? <span className="text-rose-600 font-medium">{r.overdue_time}</span> : <span className="text-gray-300">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TablePagination page={qcT.page} totalPages={qcT.totalPages} setPage={qcT.setPage} total={qcT.total} pageSize={qcT.pageSize} testId="tcc-qc-pagination" />
          </Section>

          {/* GTM */}
          {!isCxLeader && (
          <Section title="GTM — Documents & Commercials" Icon={Briefcase} accent={{ bg: 'bg-sky-50', fg: 'text-sky-600' }} count={gtm.rows.length} testId="tcc-gtm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tcc-gtm-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left">Account</th>
                    <th className="px-4 py-2.5 text-left">Lead Owner</th>
                    <th className="px-4 py-2.5 text-left">Pending Reason</th>
                    <th className="px-4 py-2.5 text-left">Time Pending</th>
                    <th className="px-4 py-2.5 text-left">Overdue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {gtmT.total === 0 && <Empty cols={5} />}
                  {gtmT.pageRows.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-gray-800">{r.account_name}</td>
                      <td className="px-4 py-2.5 text-gray-700">{r.lead_owner}</td>
                      <td className="px-4 py-2.5 text-gray-600 text-xs">{r.pending_reason}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">{r.time_pending}</td>
                      <td className="px-4 py-2.5"><OverdueBadge label={r.overdue} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TablePagination page={gtmT.page} totalPages={gtmT.totalPages} setPage={gtmT.setPage} total={gtmT.total} pageSize={gtmT.pageSize} testId="tcc-gtm-pagination" />
          </Section>
          )}

          {/* GTM Sales Leader */}
          {!isCxLeader && (
          <Section title="GTM Sales Leader — Approvals" Icon={Crown} accent={{ bg: 'bg-amber-50', fg: 'text-amber-600' }} count={gtmLeader.rows.length} testId="tcc-gtm-leader">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tcc-gtm-leader-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left">Account</th>
                    <th className="px-4 py-2.5 text-left">Waiting Since</th>
                    <th className="px-4 py-2.5 text-left">Pending Time</th>
                    <th className="px-4 py-2.5 text-left">Overdue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {gtmLeaderT.total === 0 && <Empty cols={4} />}
                  {gtmLeaderT.pageRows.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-gray-800">{r.account_name}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">{fmtDate(r.waiting_since)}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">{r.pending_time}</td>
                      <td className="px-4 py-2.5"><OverdueBadge label={r.overdue} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TablePagination page={gtmLeaderT.page} totalPages={gtmLeaderT.totalPages} setPage={gtmLeaderT.setPage} total={gtmLeaderT.total} pageSize={gtmLeaderT.pageSize} testId="tcc-gtm-leader-pagination" />
          </Section>
          )}

          {/* CX Leader */}
          {!isCxLeader && (
          <Section title="CX Leader — Unassigned Listings" Icon={Users} accent={{ bg: 'bg-violet-50', fg: 'text-violet-600' }} count={cxLeader.rows.length} testId="tcc-cx-leader">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tcc-cx-leader-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left">Property</th>
                    <th className="px-4 py-2.5 text-left">Waiting Since</th>
                    <th className="px-4 py-2.5 text-left">Pending Duration</th>
                    <th className="px-4 py-2.5 text-left">Overdue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {cxLeaderT.total === 0 && <Empty cols={4} />}
                  {cxLeaderT.pageRows.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-gray-800">{r.property_name}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">{fmtDate(r.waiting_since)}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">{r.pending_duration}</td>
                      <td className="px-4 py-2.5"><OverdueBadge label={r.overdue} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TablePagination page={cxLeaderT.page} totalPages={cxLeaderT.totalPages} setPage={cxLeaderT.setPage} total={cxLeaderT.total} pageSize={cxLeaderT.pageSize} testId="tcc-cx-leader-pagination" />
          </Section>
          )}

          {/* BFR */}
          {!isCxLeader && (
          <Section title="BFR — Approvals & Finance" Icon={Banknote} accent={{ bg: 'bg-emerald-50', fg: 'text-emerald-600' }} count={(bfr.approvals?.length || 0) + (bfr.offboarding_finance?.length || 0)} testId="tcc-bfr">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tcc-bfr-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left">Account</th>
                    <th className="px-4 py-2.5 text-left">Type</th>
                    <th className="px-4 py-2.5 text-left">Waiting Since</th>
                    <th className="px-4 py-2.5 text-left">Pending Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {bfrT.total === 0 && <Empty cols={4} />}
                  {bfrT.pageRows.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-gray-800">{r.account_name}</td>
                      <td className="px-4 py-2.5 text-gray-600 text-xs">
                        <span className="inline-flex items-center gap-1">
                          {(r.type || '').toLowerCase().includes('offboard') && <AlertTriangle size={11} className="text-orange-500" />}
                          {r.type}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">{fmtDate(r.waiting_since)}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">{r.pending_time}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TablePagination page={bfrT.page} totalPages={bfrT.totalPages} setPage={bfrT.setPage} total={bfrT.total} pageSize={bfrT.pageSize} testId="tcc-bfr-pagination" />
          </Section>
          )}
        </>
      )}
    </div>
  );
};

export default TaskCommandCenter;
